import {
  DESIGNER_LOOKS,
  DESIGNER_PATTERN_MODES,
  type DesignImageEffect,
  type DesignLayer,
  type DesignTextLayer,
  type DesignerPattern,
} from '@pz/scene';
import {
  disclosure,
  el,
  iconBtn,
  note,
  segmented,
  switchRow,
  type DesignerCtx,
  type IconName,
} from './panels.js';
import { createColorBlock, createStepper } from './controls.js';
import { run } from './commands.js';
import {
  anchorVOf,
  canHaveFixedWidth,
  isFixedTextBox,
  minWrapWidthMm,
  setTextBox,
  setTextBoxWidth,
} from './text-box.js';
import { round, type LayerPatch } from './state.js';

/**
 * "Efekti" — the reference editor's Effects panel, one variant per kind.
 *
 * The ranges here are the reference's, translated into our model exactly once
 * (the mapping table lives in the spec and in the comments below): a stroke
 * "Thickness" of 1–200 is a width fraction of 0–0.5, a letter spacing of
 * −1…3 em is our per-mille tracking, a curve of −1…1 is our −100…100 bend.
 * Getting those conversions wrong is invisible on screen and catastrophic in
 * the print file, which is why they live in one file with the numbers written
 * down beside them.
 */

/** Zazzle's stroke thickness 1–200 maps onto our width fraction 0–0.5. */
const THICKNESS_TO_WIDTH = 400;

/** Which sections are open, per designer; a repaint must not collapse them. */
interface EffectsState {
  open: Set<string>;
}
const states = new WeakMap<DesignerCtx, EffectsState>();

function stateFor(ctx: DesignerCtx): EffectsState {
  const existing = states.get(ctx);
  if (existing) return existing;
  // Everything is open by default except the two the reference collapses.
  const fresh: EffectsState = { open: new Set(['opacity', 'tiling', 'shadow', 'stroke', 'spacing', 'textBox', 'curve', 'orientation', 'filters']) };
  states.set(ctx, fresh);
  return fresh;
}

function section(
  ctx: DesignerCtx,
  body: HTMLElement,
  id: string,
  title: string,
  build: (into: HTMLElement) => void,
): void {
  const state = stateFor(ctx);
  const open = state.open.has(id);
  const box = disclosure(title, open, (next) => {
    if (next) state.open.add(id);
    else state.open.delete(id);
    ctx.refreshPanel();
  });
  if (open) build(box.body);
  body.append(box.wrap);
}

export function renderEffectsPanel(body: HTMLElement, ctx: DesignerCtx): void {
  const layer = ctx.selected();
  if (!layer) {
    body.append(note(ctx.texts.selectLayer));
    return;
  }
  const ids = [layer.id];
  const patch = (changes: LayerPatch, tag?: string): void => {
    ctx.dispatch({ type: 'update', id: layer.id, patch: changes, ...(tag ? { tag } : {}) });
  };

  opacitySection(ctx, body, layer);
  if (ctx.config.allow.pattern && layer.kind !== 'qr') tilingSection(ctx, body, layer);

  if (layer.kind === 'text') {
    if (ctx.config.text.allowShadow) shadowSection(ctx, body, layer);
    strokeSection(ctx, body, layer);
    spacingSection(ctx, body, layer);
    if (canHaveFixedWidth(layer)) textBoxSection(ctx, body, layer);
    // A wrapped column has no single arc for its words to follow, and the
    // model refuses the pair outright — so the control goes rather than
    // silently losing the bend the shopper just dialled in.
    if (ctx.config.text.allowCurve && layer.orientation !== 'vertical' && !isFixedTextBox(layer)) {
      curveSection(ctx, body, layer, patch);
    }
    orientationSection(ctx, body, layer, ids);
  }

  if (layer.kind === 'image') imageSections(ctx, body, layer, patch);
}

// ── Shared sections ──────────────────────────────────────────────────────────

function opacitySection(ctx: DesignerCtx, body: HTMLElement, layer: DesignLayer): void {
  section(ctx, body, 'opacity', ctx.texts.opacity, (into) => {
    // One opacity in the whole designer: the model keeps 0–1, every control
    // shows 1–100, and the command layer is the only writer.
    into.append(createStepper({
      label: ctx.texts.opacity,
      value: Math.round((layer.opacity ?? 1) * 100),
      min: 1,
      max: 100,
      step: 1,
      unit: '%',
      slider: true,
      onCommit: (value) => run(ctx, {
        op: 'properties',
        ids: [layer.id],
        patch: { opacity: round(value / 100, 3) },
      }),
    }).element);
  });
}

const TILE_ICONS: Array<[DesignerPattern['mode'] | 'none', IconName, keyof DesignerCtx['texts']]> = [
  ['none', 'close', 'tileNone'],
  ['centered', 'alignCenter', 'tileCentered'],
  ['basic', 'copy', 'tileBasic'],
  ['half-brick', 'alignLeft', 'tileHalfBrick'],
  ['half-drop', 'alignCenter', 'tileHalfDrop'],
  ['mirror', 'flipH', 'tileMirror'],
];

function tilingSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignLayer): void {
  section(ctx, body, 'tiling', ctx.texts.tiling, (into) => {
    const current = layer.pattern?.mode ?? 'none';
    const base: DesignerPattern = layer.pattern ?? { mode: 'basic', gapXmm: 2, gapYmm: 2 };
    const tiles = el('div', 'pzd-tile-row');
    for (const [mode, icon, labelKey] of TILE_ICONS) {
      if (mode !== 'none' && !DESIGNER_PATTERN_MODES.includes(mode)) continue;
      const label = String(ctx.texts[labelKey]);
      const tile = el('button', `pzd-mode-tile${current === mode ? ' is-on' : ''}`);
      tile.type = 'button';
      tile.setAttribute('aria-pressed', String(current === mode));
      tile.setAttribute('aria-label', label);
      tile.innerHTML = patternPreview(mode);
      tile.append(el('span', undefined, label));
      tile.addEventListener('click', () => run(ctx, {
        op: 'pattern',
        ids: [layer.id],
        pattern: mode === 'none' ? null : { ...base, mode },
      }));
      tiles.append(tile);
    }
    into.append(tiles);
    if (!layer.pattern) return;
    const gaps = layer.pattern;
    section(ctx, into, 'tiling-advanced', ctx.texts.advanced, (deep) => {
      deep.append(
        createStepper({
          label: ctx.texts.gapX,
          value: gaps.gapXmm,
          min: 0,
          max: 200,
          step: 0.5,
          unit: 'mm',
          slider: true,
          onCommit: (gapXmm) => run(ctx, { op: 'pattern', ids: [layer.id], pattern: { ...gaps, gapXmm } }),
        }).element,
        createStepper({
          label: ctx.texts.gapY,
          value: gaps.gapYmm,
          min: 0,
          max: 200,
          step: 0.5,
          unit: 'mm',
          slider: true,
          onCommit: (gapYmm) => run(ctx, { op: 'pattern', ids: [layer.id], pattern: { ...gaps, gapYmm } }),
        }).element,
      );
    });
  });
}

/** A 24-box sketch of how the tiles sit, so the mode is readable at a glance. */
function patternPreview(mode: DesignerPattern['mode'] | 'none'): string {
  const dot = (x: number, y: number): string => `<rect x="${x}" y="${y}" width="6" height="6" rx="1"/>`;
  let body: string;
  if (mode === 'none') body = dot(9, 9);
  else if (mode === 'basic') body = dot(3, 3) + dot(15, 3) + dot(3, 15) + dot(15, 15);
  // Centred: one whole tile in the middle, halves at the edges.
  else if (mode === 'centered') body = dot(9, 9) + `<rect x="0" y="9" width="3" height="6" rx="1"/>`
    + `<rect x="21" y="9" width="3" height="6" rx="1"/><rect x="9" y="0" width="6" height="3" rx="1"/>`
    + `<rect x="9" y="21" width="6" height="3" rx="1"/>`;
  else if (mode === 'half-brick') body = dot(3, 3) + dot(15, 3) + dot(9, 15);
  else if (mode === 'half-drop') body = dot(3, 3) + dot(3, 15) + dot(15, 9);
  else body = dot(3, 3) + `<g transform="translate(24,0) scale(-1,1)">${dot(3, 3)}</g>` + dot(3, 15)
    + `<g transform="translate(24,0) scale(-1,1)">${dot(3, 15)}</g>`;
  return `<svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">${body}</svg>`;
}

// ── Text sections ────────────────────────────────────────────────────────────

function shadowSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignTextLayer): void {
  section(ctx, body, 'shadow', ctx.texts.shadow, (into) => {
    const on = Boolean(layer.shadow);
    into.append(switchRow(ctx.texts.shadowSwitch, on, (next) => run(ctx, {
      op: 'text',
      ids: [layer.id],
      // The reference's defaults: direction 45°, offset 55, a light grey.
      patch: { shadow: next ? { color: { hex: '#9a9a9a' }, offset: 55, angle: 45, blur: 0 } : null },
    })));
    const shadow = layer.shadow;
    if (!shadow) return;
    const write = (changes: Partial<NonNullable<DesignTextLayer['shadow']>>): void => {
      run(ctx, { op: 'text', ids: [layer.id], patch: { shadow: { ...shadow, ...changes } } });
    };
    into.append(createColorBlock({
      ctx,
      value: shadow.color,
      palette: ctx.config.text.outlinePalette,
      onPick: (color) => write({ color: color ?? { hex: '#000000' } }),
    }).element);
    into.append(createStepper({
      label: ctx.texts.shadowDirection,
      value: Math.round(shadow.angle),
      min: -180,
      max: 180,
      step: 1,
      unit: '°',
      slider: true,
      onCommit: (angle) => write({ angle }),
    }).element);
    into.append(createStepper({
      label: ctx.texts.shadowOffset,
      value: Math.round(shadow.offset),
      min: 1,
      max: 100,
      step: 1,
      slider: true,
      onCommit: (offset) => write({ offset }),
    }).element);
    // Ours, not the reference's: our renderer blurs the shadow, and a hard
    // shadow at offset 55 with no blur looks like a printing mistake.
    into.append(createStepper({
      label: ctx.texts.blurAmount,
      value: Math.round(shadow.blur),
      min: 0,
      max: 100,
      step: 1,
      slider: true,
      onCommit: (blur) => write({ blur }),
    }).element);
  });
}

function strokeSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignTextLayer): void {
  section(ctx, body, 'stroke', ctx.texts.strokeSwitch, (into) => {
    const on = Boolean(layer.outline && layer.outline.width > 0);
    into.append(switchRow(ctx.texts.strokeSwitch, on, (next) => run(ctx, {
      op: 'text',
      ids: [layer.id],
      patch: { outline: next ? { color: { hex: '#b4b4b4' }, width: 75 / THICKNESS_TO_WIDTH } : null },
    })));
    const outline = layer.outline;
    if (!on || !outline) return;
    into.append(createColorBlock({
      ctx,
      value: outline.color,
      palette: ctx.config.text.outlinePalette,
      onPick: (color) => run(ctx, {
        op: 'text',
        ids: [layer.id],
        patch: { outline: { color: color ?? { hex: '#000000' }, width: outline.width } },
      }),
    }).element);
    into.append(createStepper({
      label: ctx.texts.thickness,
      value: Math.round(outline.width * THICKNESS_TO_WIDTH),
      min: 1,
      max: 200,
      step: 1,
      slider: true,
      onCommit: (thickness) => run(ctx, {
        op: 'text',
        ids: [layer.id],
        patch: { outline: { color: outline.color, width: round(thickness / THICKNESS_TO_WIDTH, 4) } },
      }),
    }).element);
  });
}

function spacingSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignTextLayer): void {
  section(ctx, body, 'spacing', ctx.texts.spacing, (into) => {
    into.append(createStepper({
      label: ctx.texts.lineSpacingLabel,
      value: round(layer.lineSpacing ?? 1.15, 2),
      min: 0.5,
      max: 3,
      step: 0.05,
      slider: true,
      onCommit: (lineSpacing) => run(ctx, { op: 'text', ids: [layer.id], patch: { lineSpacing } }),
    }).element);
    // The reference measures letter spacing in em (−1…3); our model carries
    // per-mille of the em, so the field shows em and multiplies by 1000.
    into.append(createStepper({
      label: ctx.texts.letterSpacing,
      value: round((layer.tracking ?? 0) / 1000, 2),
      min: -0.5,
      max: 1,
      step: 0.01,
      unit: 'em',
      slider: true,
      onCommit: (em) => run(ctx, { op: 'text', ids: [layer.id], patch: { tracking: Math.round(em * 1000) } }),
    }).element);
  });
}

/**
 * "Okvir teksta" — the switch that turns an auto box into a wrap column, and
 * back.
 *
 * The reference editor has no such switch: its boxes simply become fixed the
 * first time a side handle is dragged, and there is no way back. Ours keeps
 * the same gesture (dragging a side handle on a text layer makes it a column)
 * but shows the state here and offers the way back, because a shopper who
 * widened a name by accident should not have to delete it and start again.
 */
function textBoxSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignTextLayer): void {
  section(ctx, body, 'textBox', ctx.texts.textBoxSection, (into) => {
    const fixed = isFixedTextBox(layer);
    into.append(switchRow(ctx.texts.fixedWidthSwitch, fixed, (on) => {
      setTextBox(ctx, [layer.id], {
        boxMode: on ? 'fixed' : 'auto',
        ...(on ? { widthMm: round(layer.box.w, 2) } : {}),
      });
      ctx.refreshPanel();
    }));
    into.append(note(fixed ? ctx.texts.fixedWidthOn : ctx.texts.fixedWidthOff));
    if (fixed) {
      into.append(createStepper({
        label: ctx.texts.wrapWidth,
        value: round(layer.box.w, 1),
        min: minWrapWidthMm(layer.sizeMm),
        max: round(Math.max(ctx.config.sizeMm.w * 2, layer.box.w), 1),
        step: 0.5,
        unit: 'mm',
        onCommit: (widthMm) => setTextBoxWidth(ctx, layer, widthMm, `wrap:${layer.id}`),
      }).element);
    }
    // The anchor decides which edge stays put while the lines come and go, so
    // it belongs beside the thing that changes the line count.
    const anchors = el('div', 'pzd-icon-row');
    const current = anchorVOf(layer);
    const rows: Array<['alignTop' | 'alignMiddle' | 'alignBottom', string, 'top' | 'middle' | 'bottom']> = [
      ['alignTop', ctx.texts.anchorTop, 'top'],
      ['alignMiddle', ctx.texts.anchorMiddle, 'middle'],
      ['alignBottom', ctx.texts.anchorBottom, 'bottom'],
    ];
    for (const [icon, label, value] of rows) {
      anchors.append(iconBtn(icon, label, () => {
        setTextBox(ctx, [layer.id], { anchorV: value });
        ctx.refreshPanel();
      }, { pressed: current === value }));
    }
    into.append(el('span', 'pzd-hint', ctx.texts.verticalAnchor), anchors, note(ctx.texts.anchorHint));
  });
}

function curveSection(
  ctx: DesignerCtx,
  body: HTMLElement,
  layer: DesignTextLayer,
  patch: (changes: LayerPatch, tag?: string) => void,
): void {
  section(ctx, body, 'curve', ctx.texts.curvedText, (into) => {
    // The reference's −1…1; our bend is −100…100 of the same thing.
    into.append(createStepper({
      label: ctx.texts.curvedText,
      value: round((layer.bend ?? 0) / 100, 2),
      min: -1,
      max: 1,
      step: 0.01,
      slider: true,
      onInput: (value) => patch({ bend: Math.round(value * 100) || undefined }, `bend:${layer.id}`),
      onCommit: (value) => patch({ bend: Math.round(value * 100) || undefined }, `bend:${layer.id}`),
    }).element);
  });
}

function orientationSection(ctx: DesignerCtx, body: HTMLElement, layer: DesignTextLayer, ids: string[]): void {
  section(ctx, body, 'orientation', ctx.texts.orientationLabel, (into) => {
    const vertical = layer.orientation === 'vertical';
    into.append(segmented([
      { id: 'horizontal', label: ctx.texts.horizontal, on: !vertical },
      { id: 'vertical', label: ctx.texts.vertical, on: vertical },
    ], (value) => run(ctx, {
      op: 'orientation',
      ids,
      orientation: value === 'vertical' ? 'vertical' : 'horizontal',
    })));
  });
}

// ── Image sections ───────────────────────────────────────────────────────────

function hasEffect(effects: DesignImageEffect[] | undefined, kind: string): boolean {
  return (effects ?? []).some((effect) => effect.kind === kind);
}

function withoutKinds(effects: DesignImageEffect[] | undefined, kinds: string[]): DesignImageEffect[] {
  return (effects ?? []).filter((effect) => !kinds.includes(effect.kind));
}

function amountOf(effects: DesignImageEffect[] | undefined, kind: 'brightness' | 'contrast'): number {
  for (const effect of effects ?? []) {
    if (effect.kind === kind) return effect.amount;
  }
  return 0;
}

function imageSections(
  ctx: DesignerCtx,
  body: HTMLElement,
  layer: Extract<DesignLayer, { kind: 'image' }>,
  patch: (changes: LayerPatch, tag?: string) => void,
): void {
  if (!ctx.config.image.allowFilters) return;
  section(ctx, body, 'filters', ctx.texts.filters, (into) => {
    // Zazzle's filter strip: no filter, black & white, sepia and the named
    // looks, each tile showing THIS photo in that look. One at a time; the
    // shared `imageEffects` command owns the replacement rule.
    into.append(createLookGrid(ctx, layer, (look) => run(ctx, { op: 'imageEffects', ids: [layer.id], look })));

    // "Remove white from image": never, the background only (white connected
    // to the picture's edge), or every white pixel. Native in preview and print.
    const white = (layer.effects ?? []).find((effect) => effect.kind === 'whiteTransparent');
    const mode = !white ? 'never' : 'connected' in white && white.connected ? 'border' : 'all';
    into.append(el('div', 'pzd-lbl', ctx.texts.whiteRemoval));
    into.append(segmented([
      { id: 'never', label: ctx.texts.whiteNever, on: mode === 'never' },
      { id: 'border', label: ctx.texts.whiteBorder, on: mode === 'border' },
      { id: 'all', label: ctx.texts.whiteAll, on: mode === 'all' },
    ], (value) => run(ctx, {
      op: 'imageEffects',
      ids: [layer.id],
      whiteRemoval: value === 'never' ? null : value === 'border' ? 'border' : 'all',
    })));
    into.append(note(ctx.texts.whiteRemovalNote));

    // Aspect ratio: with it on the side handles disappear, which is exactly
    // what the reference's "Aspect ratio" toggle does.
    into.append(switchRow(ctx.texts.keepAspect, ctx.view.lockAspect, (on) => ctx.setView({ lockAspect: on })));

    section(ctx, into, 'image-advanced', ctx.texts.advanced, (deep) => {
      for (const kind of ['brightness', 'contrast'] as const) {
        deep.append(createStepper({
          label: kind === 'brightness' ? ctx.texts.brightness : ctx.texts.contrast,
          value: Math.round(amountOf(layer.effects, kind) * 100),
          min: -100,
          max: 100,
          step: 1,
          slider: true,
          onCommit: (value) => {
            const rest = withoutKinds(layer.effects, [kind]);
            const amount = round(value / 100, 2);
            const next = amount === 0 ? rest : [...rest, { kind, amount } as DesignImageEffect];
            patch({ effects: next.length ? next : undefined }, `${kind}:${layer.id}`);
          },
        }).element);
      }
    });
  });
}

/** The look a photo layer currently has: a look id, `grayscale`, `sepia` or null. */
export function currentLook(effects: DesignImageEffect[] | undefined): string | null {
  for (const effect of effects ?? []) {
    if (effect.kind === 'look') return effect.id;
    if (effect.kind === 'grayscale' || effect.kind === 'sepia') return effect.kind;
  }
  return null;
}

/** Skia's matrix, written for an SVG `feColorMatrix` (same 4×5 layout, offsets 0–1). */
function svgMatrix(matrix: readonly number[]): string {
  return matrix.map((n) => String(Math.round(n * 1e4) / 1e4)).join(' ');
}

/**
 * The look tiles, each a thumbnail of the shopper's own photo.
 *
 * The thumbnails are the browser's — an SVG colour matrix on an `<img>`, the
 * very matrix the renderer applies — so twelve previews cost nothing; the
 * canvas and the print use the native renderer as for every other effect.
 * `color-interpolation-filters: sRGB` makes the browser do the arithmetic in
 * the same space Skia does.
 */
export function createLookGrid(
  ctx: DesignerCtx,
  layer: Extract<DesignLayer, { kind: 'image' }>,
  onPick: (look: string | null) => void,
): HTMLElement {
  const wrap = el('div', 'pzd-look-grid');
  wrap.setAttribute('role', 'radiogroup');
  wrap.setAttribute('aria-label', ctx.texts.filters);
  const url = ctx.renderer.assetUrl(layer.assetId);
  const current = currentLook(layer.effects);
  const uid = `pzd-look-${Math.random().toString(36).slice(2, 8)}`;
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  defs.setAttribute('width', '0');
  defs.setAttribute('height', '0');
  defs.setAttribute('aria-hidden', 'true');
  defs.style.position = 'absolute';
  defs.innerHTML = DESIGNER_LOOKS.map((look) =>
    `<filter id="${uid}-${look.id}" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="${svgMatrix(look.matrix)}"/></filter>`,
  ).join('');
  wrap.append(defs);
  const tiles: Array<{ id: string | null; label: string; css: string }> = [
    { id: null, label: ctx.texts.filterNone, css: '' },
    { id: 'grayscale', label: ctx.texts.filterGray, css: 'grayscale(1)' },
    { id: 'sepia', label: ctx.texts.filterSepia, css: 'sepia(1)' },
    ...DESIGNER_LOOKS.map((look) => ({ id: look.id, label: look.name, css: `url(#${uid}-${look.id})` })),
  ];
  for (const tile of tiles) {
    const on = tile.id === current;
    const button = el('button', `pzd-look${on ? ' is-on' : ''}`);
    button.type = 'button';
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', String(on));
    button.setAttribute('aria-label', tile.label);
    const thumb = el('span', 'pzd-look-thumb');
    if (url) {
      const img = el('img');
      img.src = url;
      img.alt = '';
      img.draggable = false;
      if (tile.css) img.style.filter = tile.css;
      thumb.append(img);
    }
    button.append(thumb, el('span', 'pzd-look-name', tile.label));
    button.addEventListener('click', () => {
      if (!on) onPick(tile.id);
    });
    wrap.append(button);
  }
  return wrap;
}

/** The shape variant has no extra sections of its own beyond opacity/tiling. */
export function effectsTitleFor(ctx: DesignerCtx): string {
  const layer = ctx.selected();
  if (layer?.kind === 'image') return ctx.texts.imageEffects;
  if (layer?.kind === 'shape') return ctx.texts.shapeEffects;
  if (layer?.kind === 'text') return ctx.texts.textEffects;
  return ctx.texts.effectsTool;
}

/** Re-exported for the toolbar, which shows the same conversion inline. */
export { THICKNESS_TO_WIDTH };
